import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay3() {
  const { lesson, course, accent } = findLesson("sing-and-play", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/sing-and-play/SP3-Oom-Pah-Sunshine-Animated.mp4"
        caption={`Dominant 7th — add the ♭7 to V for stronger pull home`}
        hint="≈ 26 sec · D becomes D7"
      />
      {/* ── 1. Context ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="The big idea" title="You Are My Sunshine — the engine and the passenger">
        <p>
          Three lessons in and you now know the Oom-Pah at a conceptual level. This lesson
          fixes it in your muscle memory through one of the most-sung songs in the English
          language. You Are My Sunshine has been recorded by Ray Charles, Jimmie Davis, Johnny
          Cash, and more children's choirs than can be counted.
        </p>
        <p>
          The C major version is our playground today — white keys only, nothing complicated.
          But the lesson inside it is one of the most important in this series:{" "}
          <strong>the left hand is the engine; the right hand is the passenger.</strong> The
          engine doesn't wait for the passenger. It goes. The passenger catches up.
        </p>
        <SAPQuote>
          It's just going to be between the 1, the 4 and the 5, and sometimes the 5
          would end up being a 7th chord of the 5.
        </SAPQuote>
      </SAPSection>

      {/* ── 2. Melody note insight ─────────────────────────────────────── */}
      <SAPSection eyebrow="Ear training" title="The melody starts on the 3rd — not the root">
        <p>
          Here's something most people don't notice: <em>"You are my sun-shine"</em> — the word
          "You" starts on <strong>E</strong>, not on C. That's the 3rd scale degree of C major.
          The song begins on a colour note, not on home base.
        </p>
        <p>
          Why does this matter? Because when you're playing and singing simultaneously, you need
          to know <em>where to find your first note</em> without searching for it. The chord
          under it is C major — but your voice enters on E. Find that E before you start. Hear
          it internally, then sing it. The piano gives you the harmonic context; your ear places
          the melody note inside it.
        </p>
        <SAPCallout label="Starting note drill" variant="insight">
          Play a C major chord with your right hand. Without stopping the Oom-Pah, sing just the
          note E (third of the chord). That's your entry note for the melody. Practice finding it
          silently before you start the song every time.
        </SAPCallout>
      </SAPSection>

      {/* ── 3. Reference recording ─────────────────────────────────────── */}
      <SAPSection eyebrow="Listen first" title="Ray Charles — the definitive version">
        <p>
          Ray Charles recorded this as <em>Georgia On My Mind</em>'s quieter cousin — slow,
          warm, conversational. Listen to how his left hand sits completely steady under the
          melody. There's no hurry in the bass. The chord changes are announced, not rushed.
          That steadiness is what you're building.
        </p>
        <YouTubeEmbed
          videoId="viyvsknvSYs"
          title="Ray Charles — You Are My Sunshine"
          caption="The gold standard. Notice how unhurried and steady the accompaniment feels underneath the melody."
        />
      </SAPSection>

      {/* ── 4. The chords ──────────────────────────────────────────────── */}
      <SAPSection eyebrow="The harmony" title="C major — I, IV, V7 with a lift chord">
        <p>
          C, F, and G7 — that's the whole song. The verse holds on C for the opening phrase,
          moves to F (IV) for colour and back to C, then the G7 (V7) cadences home. Just like
          I'll Fly Away, there's a lift chord before the chorus: <strong>C7</strong>, which
          borrows the flat-7th (B♭) and creates forward motion into the F chord on the chorus.
        </p>
        <p>
          Think of it this way — C major is the home; F is a warm room inside the home; G7 is
          the front door (it wants to close back to C). The C7 is you turning the door handle —
          it prepares the move before you step through.
        </p>

        <SAPChordRow
          label="Full chord vocabulary · C major"
          chords={[
            { label: "C",  rn: "I",   pitches: ["C4","E4","G4","C5"], fn: "tonic" },
            { label: "F",  rn: "IV",  pitches: ["F3","A3","C4","F4"], fn: "subdominant" },
            { label: "G7", rn: "V7",  pitches: ["G3","B3","D4","F4"], fn: "dominant" },
            { label: "C7", rn: "I7",  pitches: ["C4","E4","G4","Bb4"], fn: "special" },
          ]}
          caption="C7 is the lift chord — play it right before the chorus F. The Bb creates forward motion."
        />

        <RomanNumeralAnalysis
          label="Verse · C major"
          caption="The song stays on I for the first phrase, then visits IV before the V7 cadence closes each section."
          chords={[
            { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4","E4","G4","C5"] },
            { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4","E4","G4","C5"] },
            { bar: true },
            { chord: "F",  rn: "IV",  function: "Pre-dominant", pitches: ["F3","A3","C4","F4"] },
            { chord: "F",  rn: "IV",  function: "Pre-dominant", pitches: ["F3","A3","C4","F4"] },
            { bar: true },
            { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4","E4","G4","C5"] },
            { chord: "C7", rn: "I7",  function: "Secondary V",  pitches: ["C4","E4","G4","Bb4"] },
            { bar: true },
            { chord: "F",  rn: "IV",  function: "Pre-dominant", pitches: ["F3","A3","C4","F4"] },
            { bar: true },
            { chord: "G7", rn: "V7",  function: "Dominant",     pitches: ["G3","B3","D4","F4"] },
            { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4","E4","G4","C5"] },
          ]}
        />
      </SAPSection>

      {/* ── 5. Interactive loop ────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive" title="The Oom-Pah loop — C major">
        <p>
          This is the warmest of the three loops so far. C major lives in the middle of the
          piano — nothing too high, nothing too low. The bass alternates C and G (root and fifth)
          under the chord changes. Hit Play and try to hum the melody on top.
        </p>
        <OomPahPlayer
          progression={[
            { label: "C",  bars: 2, lhRoot: "C3", lhFifth: "G3", rh: ["E4","G4","C5"] },
            { label: "F",  bars: 1, lhRoot: "F3", lhFifth: "C4", rh: ["A4","C5","F5"] },
            { label: "C",  bars: 1, lhRoot: "C3", lhFifth: "G3", rh: ["E4","G4","C5"] },
            { label: "G7", bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["B4","D5","F5"] },
            { label: "C",  bars: 1, lhRoot: "C3", lhFifth: "G3", rh: ["E4","G4","C5"] },
          ]}
          bpm={84}
          label="YOU ARE MY SUNSHINE — C major · 6-bar loop"
        />
        <SAPCallout label="Singing entry" variant="tip">
          The moment you hit Play, count two bars silently, then enter with <em>"You are my…"</em>
          on the E above the C chord. If your voice wavers, don't look down at your hands — your
          hands are fine; they're on autopilot. Eyes up, voice forward.
        </SAPCallout>
      </SAPSection>

      {/* ── 6. Engine-and-passenger drill ──────────────────────────────── */}
      <SAPSection eyebrow="Technique" title="The engine-and-passenger exercise">
        <p>
          Here's a drill that isolates the independence problem. Play C–G–C–G in the left hand
          at 70 bpm, metronomic. Now, with the right hand, play a random note on every off-beat —
          any note, doesn't matter. Then start changing the right-hand note freely while the left
          hand stays locked.
        </p>
        <p>
          What you're training is the disconnection. Your left hand must become unconscious — like
          your foot on the gas pedal. You don't think about your foot; you think about the road.
          The road is the melody. The foot is the left hand.
        </p>

      </SAPSection>

      {/* ── 7. Practice plan ───────────────────────────────────────────── */}

      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/03-you-are-my-sunshine.png"
          caption="Jason's chord chart — You Are My Sunshine"
        />
      </SAPSection>
      <SAPSection eyebrow="Practice plan" title="From white keys to full song">
        <SAPPracticeLadder
          intro="This is your cleanest key — C major, all white keys. Nail the engine-passenger separation here before moving to harder keys."
          rungs={[
            {
              title: "Find the starting note",
              minutes: 3,
              detail: "Play a C major chord, sing the E (third of chord). Repeat until you can find it instantly. This is your song entry.",
              accent: "amber",
            },
            {
              title: "LH pattern — C, F, G7",
              minutes: 5,
              detail: "Alternating bass (C–G, F–C, G–D) through the chord changes. 60 bpm, no right hand.",
              accent: "amber",
            },
            {
              title: "Full Oom-Pah loop",
              minutes: 8,
              detail: "Both hands together, 72 bpm. C – F – C – G7 – C loop. Add C7 before the F. Three times through without stopping.",
              accent: "purple",
            },
            {
              title: "Hum the melody over the loop",
              minutes: 7,
              detail: "Keep hands playing. Hum only — no words. Find the E entry, follow the melody contour. Your hands don't stop.",
              accent: "purple",
            },
            {
              title: "Sing words at full speed",
              minutes: 10,
              detail: "Add lyrics. Eyes off the keys. Trust the pattern. Bring it up to 90 bpm if it feels solid at 72.",
              accent: "emerald",
            },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
